import { logger } from '../logger';

const log = logger.sub("UpsRestClient");

export class UpsRestClient {
    private readonly baseUrl?: string;
    private readonly headers: any;
    private readonly cubeId?: string;

    constructor(baseUrl?: string, token?: string, cubeId?: string) {
        this.baseUrl = baseUrl;
        this.cubeId = cubeId;
        this.headers = {
            "Authorization": `Bearer ${token}`,
            "Accept": "application/json",
            "Content-Type": "application/json",
        };
    }

    public async sendScanEvent(scanRequest: ScanRequest): Promise<UpsApiResponse> {
        const requestURL = `${this.baseUrl}/api/parcel-locker/ups-v1/${this.cubeId}/send-scan-request`;
        log.info(`Sending POST to ${requestURL} with body: ${JSON.stringify(scanRequest)}`);
        const response = await window.fetch(requestURL, {
            headers: this.headers,
            method: 'POST',
            body: JSON.stringify(scanRequest),
        });
        const responseJson = await response.json();
        log.info(`Received response to POST to ${requestURL}: ${JSON.stringify(responseJson)}`);
        return { ok: response.ok, body: responseJson };
    }

    public async getCommercialInvoice(commercialInvoiceRequest: CommercialInvoiceRequest): Promise<UpsApiResponse> {
        try {
            const requestURL = `${this.baseUrl}/api/parcel-locker/ups-v1/${this.cubeId}/send-get-commercial-invoice-request`;
            log.info(`Sending POST to ${requestURL} with body: ${JSON.stringify(commercialInvoiceRequest)}`);
            const response = await window.fetch(requestURL, {
                headers: this.headers,
                method: 'POST',
                body: JSON.stringify(commercialInvoiceRequest),
            });
            const responseJson = await response.json();
            log.info(`Received response to POST to ${requestURL}: ${JSON.stringify(responseJson)}`);
            return { ok: response.ok, body: responseJson };
        } catch (err) {
            // the process should not fail, if the commercial invoice call fails, but we need to treat it, as if the invoice was not required
            return {
                ok: false,
                body: {
                    commercialInvoiceIndication: false
                }
            }
        }
    }

    public async getReceipt(getReceiptRequest: GetReceiptRequest): Promise<UpsApiResponse> {
        const requestURL = `${this.baseUrl}/api/parcel-locker/ups-v1/${this.cubeId}/send-get-receipt-request`;
        log.info(`Sending POST to ${requestURL} with body: ${JSON.stringify(getReceiptRequest)}`);
        const response = await window.fetch(requestURL, {
            headers: this.headers,
            method: 'POST',
            body: JSON.stringify(getReceiptRequest),
        });
        const responseJson = await response.json();
        log.info(`Received response to POST to ${requestURL}: ${JSON.stringify(responseJson)}`);
        return { ok: response.ok, body: responseJson };
    }
}

export interface UpsApiResponse {
    ok: boolean;
    body: Record<string, any>;
}

export interface CommercialInvoiceRequest {
    trackingNumber: string;
}

export interface ScanRequest {
    scanDateTime: string;
    trackingNumber: string;
    event: ScanEventType;
    customerName?: string;
    emailAddress?: string;
    clarifiedSignature?: string;
    digitalSignaturePoints?: string; // base64 encoded, will be decoded and encrypted in the backend
    epraPIN?: string;
}

export interface GetReceiptRequest {
    requestType: ReceiptRequestType;
    requestedDate: string;
    serviceType: ReceiptServiceType;
    trackingInfoList: TrackingInfo[];
    emailAddress?: string;
    phoneNumber?: string;
    langCodes: string[];
}

export interface TrackingInfo {
    trackingNumber: string;
}

export type ReceiptServiceType = 'PICKUP' | 'DROPOFF';

export type ReceiptRequestType = 'EMAIL' | 'SMS';

export type ScanEventType = 'AP_POSSESSION_FROM_UPS' | 'SHIPPER_DROP_OFF' | 'SHIPPER_RECOVERY' | 'CONSIGNEE_PICKUP';